import { AccountInformation, KlavioEventProperty, KlaviyoPropertyKey, KlayvioEvents, WebError } from '../../types';
import { getFromLocalStorage, isValidString, removeLocalStorage } from '../utils';

import { Base64, SHA256 } from 'crypto-es';
import organizationInfo from '../state/organization-info';
import topicData from '../state/topic-data';
import { endSession } from '../auth-cookie';
import { ensureKlaviyo } from '../vendor-scripts';

export function getAccountInformation() {
    const accountInformationString = getFromLocalStorage('accountInformation');

    if (accountInformationString === null) {
        return undefined;
    }

    let information;

    try {
        information = JSON.parse(accountInformationString) as AccountInformation;
    } catch (e) {
        clearAccountInformation();
        return undefined;
    }

    if (information.expires_at < new Date().toISOString()) {
        clearAccountInformation();
        return undefined;
    }

    return information;
}

export function clearAccountInformation() {
    removeLocalStorage('accountInformation');
    removeLocalStorage('deviceName');
    removeLocalStorage('deviceIp');
    removeLocalStorage('deviceLocationString');
    removeLocalStorage('organization_id');
    removeLocalStorage('email');
    endSession();
}

function applyHeaders(o: any, includeAccountInformation: boolean = true): any {
    const traceId = (window as any)?.appInsights?.context?.telemetryTrace?.traceID;

    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (traceId) {
        o.headers['X-Ai-Trace-ID'] = traceId;
    }

    const accountInformation = getAccountInformation();

    if (accountInformation != null && includeAccountInformation) {
        o.headers.Authorization = `Basic ${btoa(
            `${accountInformation.email}:${accountInformation.device_name}:${accountInformation.secret}`
        )}`;
    }

    return o;
}

export function post(d: any) {
    return applyHeaders({
        method: 'POST',
        headers: {
            'Content-type': 'application/json; charset=UTF-8',
        },
        body: JSON.stringify(d),
    });
}

export function emptyPost() {
    return applyHeaders({
        method: 'POST',
        headers: {},
    });
}

export function emptyPut() {
    return applyHeaders({
        method: 'PUT',
        headers: {},
    });
}

export function put(d: any) {
    return applyHeaders({
        method: 'PUT',
        headers: {
            'Content-type': 'application/json; charset=UTF-8',
        },
        body: JSON.stringify(d),
    });
}

export function del() {
    return applyHeaders({
        method: 'DELETE',
        headers: {},
    });
}

export function delWithData(d: any) {
    return applyHeaders({
        method: 'DELETE',
        headers: {},
        body: JSON.stringify(d),
    });
}

export function get(includeAccountInformation: boolean = true) {
    return applyHeaders(
        {
            method: 'GET',
            headers: {},
        },
        includeAccountInformation
    );
}

export function externalGet(): Partial<RequestInit> {
    return {
        method: 'GET',
        mode: 'cors',
    };
}

export function logWithAmplitude(
    amplitude_callsite: string,
    message: string,
    logged_parameters: Object = {}
) {
    // we stopped calling amplitude b/c we stopped using them. Leaving this here in case we want to instrument in the future.
}

export enum GoogleTagEvent {
    // general ones (used internally for both users and admins)
    MAP_VIEW = 'proxi_map_view',
    MAP_RENDER = 'proxi_map_render',

    // Internal ones (used internally for our tracking)
    ADMIN_SHARE_CLICK = 'proxi_admin_share_click',
    UPGRADE_CLICK = 'proxi_upgrade_click',
    SUBSCRIPTION_PAGE_VISIT = 'proxi_subscription_page_visit',
    UPDATE_SUBSCRIPTION_CLICK = 'proxi_update_subscription_click',
    CHECKOUT_WITH_NEW_SUBSCRIPTION_CLICK = 'proxi_checkout_with_new_subscription_click',
    CHECKOUT_WITH_EXISTING_SUBSCRIPTION_CLICK = 'proxi_checkout_with_existing_subscription_click',
    CHECKOUT_WITH_NEW_SUCCESS = 'proxi_checkout_with_new_success',
    CHECKOUT_WITH_EXISTING_SUCCESS = 'proxi_checkout_with_existing_success',
    CHECKOUT_ABANDONED = 'proxi_checkout_abandoned',
    ADMIN_POINT_ADDED = 'proxi_admin_point_added',
    ADMIN_MANY_POINTS_ADDED = 'proxi_admin_many_points_added',
    POINT_LIMIT_REACHED_ERROR = 'proxi_point_limit_reached_error',
    TOPIC_CREATED = 'proxi_topic_created',
    TOPIC_UPDATED = 'proxi_topic_updated',
    DISCOVER_DETAILS_PAGE_VIEW = 'proxi_discover_details_page_view',
    LEGACY_CREATE_REDIRECT = 'proxi_legacy_create_redirect',
    HTTP_ERROR = 'proxi_http_error',

    // For users - tracking how their users interact with the map
    POINT_ADDED = 'proxi_analytics_point_added',
    POINT_CLICKED = 'proxi_analytics_point_clicked',
    POINT_LEAD_COLLECTED = 'proxi_analytics_point_lead_collected',
    POINT_LIKED = 'proxi_analytics_point_liked',
    POINT_MORE_INFO_CLICKED = 'proxi_analytics_point_more_info_clicked',
    POINT_GET_ROUTE_CLICKED = 'proxi_analytics_point_get_route_clicked',
    POINT_GET_DIRECTIONS_CLICKED = 'proxi_analytics_point_get_directions_clicked',
    POINT_SHARE_CLICKED = 'proxi_analytics_point_share_clicked',
    POINT_OPEN_TABLE_CLICKED = 'proxi_analytics_point_open_table_clicked',
    CUSTOM_BUTTON_CLICKED = 'proxi_analytics_point_custom_button_clicked',
    MAP_CATEGORY_CLICKED = 'proxi_analytics_map_category_clicked',
    MAP_SEARCH = 'proxi_analytics_map_search_sent',
    MAP_KEYWORD_FILTER_APPLIED = 'proxi_analytics_map_keyword_filter_applied',
    MAP_TAG_CLICKED = 'proxi_analytics_map_tag_clicked',
    MAP_SHARE_CLICKED = 'proxi_analytics_map_share_clicked',
    MAP_GATED_ACCESS_SUCCESS = 'proxi_analytics_map_gated_access_success',
    EDIT_POINT_PAGE_OPENED = 'proxi_analytics_edit_point_opened',
    POINT_FORM_VIEWED = 'proxi_analytics_point_form_viewed',
    CHALLENGE_PAGE_VIEW = 'proxi_analytics_challenge_page_view',
    REGISTRATION_PAGE_VIEW = 'proxi_analytics_registration_page_view',
    SUMMARY_PAGE_VIEW = 'proxi_analytics_summary_page_view',
    CHIP_CLICKED = 'proxi_analytics_chip_clicked',
    USER_CHECKED_IN = 'proxi_analytics_user_checked_in',

    DISCOVER_SEARCH = 'proxi_discover_search',

    MAIN_STREET_MAIN_PAGE_VISIT = 'proxi_main_street_main_page_visit',
    MAIN_STREET_REQUEST_SUBMITTED = 'proxi_main_street_feature_request_submitted',
    MAIN_STREET_SHARE_CLICKED = 'proxi_main_street_share_clicked',
    MAIN_STREET_PROXI_CLICKED = 'proxi_main_street_proxi_clicked',
}

export async function setGoogleUserId(email: string, proxi_id?: string): Promise<void> {
    try {
        const user_id = await analyticsUserId(email);
        if (!user_id) return;
        window.gtag('set', {
            user_id,
            'proxi_id': String(proxi_id),
        });
    } catch (error) {
        console.error('Error setting Google user ID:', error);
    }
}

/**
 * Analytics vendors must never receive a raw email (Google's terms forbid PII
 * in GA; a salted SHA-256 digest is what they ask for). Same salt as
 * apps/web/lib/analytics-id.ts so one person stays one user across both clients.
 */
const ANALYTICS_ID_SALT = 'proxi-ga4-2026';

async function analyticsUserId(email: string): Promise<string | null> {
    const normalized = email.trim().toLowerCase();
    if (!normalized) return null;
    const subtle: SubtleCrypto | undefined = globalThis.crypto?.subtle;
    if (subtle === undefined) return null;
    const digest = await subtle.digest('SHA-256', new TextEncoder().encode(`${ANALYTICS_ID_SALT}:${normalized}`));
    return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

export function setGoogleOrganizationId(organization_id: string, orgaization_name: string) {
    try{
        window.gtag('set', {
            'organization_id': String(organization_id),
            'organization_name': orgaization_name,
        });
    } catch (error) {
        console.error('Error setting Google user ID:', error);
    }
}

export function setGoogleSubscriptionId(subscription_id: string, subscription_status: string, plan_id?: string, customer_id?: string) {
    try{
        window.gtag('set', {
            'subscription_id': String(subscription_id),
            'plan_id': String(plan_id),
            'customer_id': String(customer_id),
            'subscription_status': String(subscription_status),
        });
    } catch (error) {
        console.error('Error setting Google user ID:', error);
    }
}


export function logWithGoogleTag(
    amplitude_callsite: string,
    event_name: GoogleTagEvent,
    logged_parameters: Record<string, any> = {}
) {
    try {
        // Create a new object with string values
        const stringParameters: Record<string, string> = {};

        // Convert all parameter values to strings
        Object.entries(logged_parameters).forEach(([key, value]) => {
            stringParameters[key] = String(value);
        });
        window.gtag('event', event_name, {
            'callsite': String(amplitude_callsite),
            ...stringParameters,
        });
    } catch (error) {
        console.error('Error logging event with Google Tag:', error);
    }
}

export function logWithGoogleTagAndTopicOrganization(
    amplitude_callsite: string,
    event_name: GoogleTagEvent,
    logged_parameters: Record<string, any> = {}
) {

    // Create a new object with string values
    const stringParameters: Record<string, string> = {};

    // Convert all parameter values to strings
    Object.entries(logged_parameters).forEach(([key, value]) => {
        stringParameters[key] = String(value);
    });


    try {
        window.gtag('event', event_name, {
            'callsite': String(amplitude_callsite),
            'topic_id': String(topicData.topic?._id.$oid),
            'topic_name': String(topicData.topic?.name),
            'is_topic_admin': String(topicData.topic?.is_writer),
            'organization_id': String(organizationInfo.organization?._id.$oid),
            'organization_name': String(organizationInfo.organization?.name),
            'is_organization_user': String(organizationInfo.organization?.is_collaborator),
            ...stringParameters,
        });
    } catch (error) {
        console.error('Error logging event with Google Tag:', error);
    }
}

export function logEventWithKlayvio(
    event_name: KlayvioEvents,
    email_address: string,
    additional_properties: KlavioEventProperty[]
) {
    ensureKlaviyo().then(() => {
        (window as any).klaviyo.identify({
            $email: email_address.toLowerCase()
        });

        // turn the additional properties into an object
        const properties: any = {};
        additional_properties.forEach((property) => {
            properties[property.key] = property.value;
        });

        (window as any).klaviyo.push(['track', event_name, properties]);
    }).catch((error) => {
        console.error('Error logging event with Klaviyo:', error);
    });
}

export function logPropertiesWithKlayvio(
    email_address: string,
    properties: Partial<Record<KlaviyoPropertyKey, string>>
) {
    ensureKlaviyo().then(() => {
        (window as any).klaviyo.identify({
            $email: email_address.toLowerCase(),
            ...properties,
        });
    }).catch((error) => {
        console.error('Error logging property with Klaviyo:', error);
    });
}

/** A failed server call, with the status so callers can tell "gone" from "flaky". */
export class HttpError extends Error {
    constructor(message: string, public readonly status: number) {
        super(message);
    }
}

export async function throwHttpError(message: string, response: Response): Promise<never> {
    const errorText = await response.text();

    let serverMessage = errorText;

    try {
        const webError = JSON.parse(errorText) as WebError;
        console.warn(webError.backtrace);
        serverMessage = webError.message;
    } catch (e) {}

    logWithAmplitude('Server Call', `Http Error Thrown`, {
        message,
        serverMessage,
    });

    logWithGoogleTagAndTopicOrganization('Server Call', GoogleTagEvent.HTTP_ERROR, {
        message,
        serverMessage,
    });

    throw new HttpError(`${message}  ${serverMessage}`, response.status);
}

export function logError(message: string) {
    logWithAmplitude('Server Call', `Error Thrown`, {
        message,
    });

    logWithGoogleTagAndTopicOrganization('Server Call', GoogleTagEvent.HTTP_ERROR, {
        message,
    });
}


export function buildQueryString(params: any) {
    let queryString = '';

    for (const key in params) {
        // eslint-disable-next-line no-prototype-builtins
        if (params.hasOwnProperty(key) === true) {
            const value = params[key] as string;
            if (isValidString(value)) {
                queryString += `${key}=${encodeURIComponent(value)}&`;
                continue;
            }

            const valueArray = params[key] as string[];
            if (Array.isArray(valueArray) && valueArray.length > 0) {
                for (const value of valueArray) {
                    queryString += `${key}=${encodeURIComponent(value)}&`;
                }
            }
        }
    }

    if (queryString.length === 0) {
        return '';
    }

    return `?${queryString}`;
}

// Memoizes an async function, using the first parameter as the cache key.
export function memoizeAsync(f: Function, keys_to_use?: number[]) {
    const cache = {} as any;

    return async (...args: any[]) => {
        let hashKey: any[] = [];
        if (keys_to_use !== undefined) {
            for (let i = 0; i < keys_to_use.length; i++) {
                hashKey.push(args[keys_to_use[i]]);
            }
        } else {
            hashKey = [...args];
        }
        const hash = SHA256(hashKey.toString());
        const hashedString = hash.toString(Base64).substring(0, 10);
        if (hashedString in cache) {
            return cache[hashedString];
        } else {
            const result = await f.apply(undefined, args);
            cache[hashedString] = result;
            return result;
        }
    };
}

// Memoizes a function, using the first parameter as the cache key.
export function memoize(f: Function) {
    const cache = {} as any;

    return (...args: any[]) => {
        const hash = SHA256(args.toString());
        const hashedString = hash.toString(Base64).substring(0, 10);

        if (hashedString in cache) {
            return cache[hashedString];
        } else {
            const result = f.apply(undefined, args);

            cache[hashedString] = result;
            return result;
        }
    };
}
